import React, { useState } from 'react';
import styled from 'styled-components';
import { Key, CheckCircle, AlertCircle, ExternalLink, Loader } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg);
  position: relative;
  overflow: hidden;
`;

const BgDecor = styled.div`
  position: absolute;
  width: 400px; height: 400px;
  border-radius: 50%;
  background: var(--gradient);
  opacity: 0.06;
  filter: blur(80px);
  &:nth-child(1) { top: -100px; left: -100px; }
  &:nth-child(2) { bottom: -150px; right: -100px; width: 500px; height: 500px; }
`;

const Card = styled.div`
  position: relative;
  background: var(--card-bg);
  border: 1px solid var(--card-border);
  border-radius: 20px;
  padding: 48px 40px;
  width: 460px;
  box-shadow: 0 8px 40px rgba(165, 145, 239, 0.15);
`;

const Logo = styled.div`
  text-align: center;
  margin-bottom: 36px;
  img {
    height: 90px;
    width: auto;
    object-fit: contain;
  }
`;

const Title = styled.h1`
  font-size: 22px;
  font-weight: 700;
  color: var(--text-dark);
  text-align: center;
  margin-bottom: 8px;
`;

const Subtitle = styled.p`
  font-size: 13px;
  color: var(--text-muted);
  text-align: center;
  margin-bottom: 32px;
  line-height: 1.5;
`;

const Label = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: var(--text-secondary);
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const InputWrap = styled.div`
  position: relative;
  margin-bottom: 20px;
  svg.icon { position: absolute; left: 14px; top: 50%; transform: translateY(-50%); color: var(--text-muted); width: 16px; height: 16px; }
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 14px 12px 40px;
  background: var(--input-bg);
  border: 1.5px solid ${p => p.$err ? 'var(--error)' : 'var(--input-border)'};
  border-radius: 10px;
  color: var(--text-primary);
  font-size: 13px;
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
  letter-spacing: 0.05em;
  outline: none;
  transition: border-color 0.2s;
  &:focus { border-color: var(--input-focus); }
  &::placeholder { color: var(--text-muted); font-family: inherit; }
`;

const Button = styled.button`
  width: 100%;
  padding: 13px;
  background: var(--btn-primary);
  color: var(--btn-text);
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: background 0.2s, transform 0.1s;
  &:hover:not(:disabled) { background: var(--btn-primary-hover); }
  &:active:not(:disabled) { transform: scale(0.98); }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const AlertBox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 10px;
  margin-bottom: 20px;
  font-size: 13px;
  line-height: 1.4;
  background: ${p => p.$type === 'error' ? 'rgba(232,114,114,0.1)' : 'rgba(124,200,142,0.1)'};
  border: 1px solid ${p => p.$type === 'error' ? 'rgba(232,114,114,0.3)' : 'rgba(124,200,142,0.3)'};
  color: ${p => p.$type === 'error' ? 'var(--error)' : 'var(--success)'};
  svg { flex-shrink: 0; margin-top: 1px; }
`;

const Footer = styled.p`
  text-align: center;
  font-size: 12px;
  color: var(--text-muted);
  margin-top: 24px;
  a {
    color: var(--primary);
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    &:hover { text-decoration: underline; }
  }
`;

export default function ActivationPage({ onActivated }) {
  const { theme } = useTheme();
  const logoSrc = theme?.isDark ? '/Qlickr-Blanc.png' : '/Qlickr-Logo-Rond-Noir.png';
  const [key, setKey] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = key.trim();
    if (!trimmed) { setError('Please enter your license key.'); return; }

    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/licensing/activate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ license_key: trimmed }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Activation failed. Please try again.');
      } else {
        setSuccess(true);
        setTimeout(() => onActivated(data.modules || []), 1200);
      }
    } catch {
      setError('Could not reach the activation server. Check your internet connection.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <BgDecor />
      <BgDecor />
      <Card>
        <Logo>
          <img src={logoSrc} alt="Qlickr" onError={e => { e.target.style.display = 'none'; }} />
        </Logo>

        <Title>Activate Qlickr Manager</Title>
        <Subtitle>
          Enter the license key you received by email after your purchase on qlickr.fr
        </Subtitle>

        {error && (
          <AlertBox $type="error">
            <AlertCircle size={15} />
            {error}
          </AlertBox>
        )}
        {success && (
          <AlertBox $type="success">
            <CheckCircle size={15} />
            License activated! Loading your workspace…
          </AlertBox>
        )}

        <form onSubmit={handleSubmit}>
          <Label htmlFor="license-key">License Key</Label>
          <InputWrap>
            <Key className="icon" />
            <Input
              id="license-key"
              type="text"
              placeholder="QLKR-XXXX-XXXX-XXXX-XXXX"
              value={key}
              onChange={e => { setKey(e.target.value); setError(''); }}
              $err={!!error}
              autoFocus
              spellCheck={false}
            />
          </InputWrap>
          <Button type="submit" disabled={loading || success}>
            {loading ? <><Loader size={15} style={{ animation: 'spin 1s linear infinite' }} /> Verifying…</> : 'Activate'}
          </Button>
        </form>

        <Footer>
          Don't have a license?{' '}
          <a href="https://qlickr.fr/pricing" target="_blank" rel="noreferrer">
            Get one at qlickr.fr <ExternalLink size={11} />
          </a>
        </Footer>
      </Card>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </Container>
  );
}
